// src/components/dashboard/DashboardAlerts.js

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../contexts/DashboardContext';
import '../../styles/dashboard-widgets.css';

const DashboardAlerts = () => {
  const { t, i18n } = useTranslation('dashboard');

  const {
    alerts,
    alertsLoading,
    alertsError,
    getAlertsBySeverity,
    formatTime
  } = useDashboard();

  const [severityFilter, setSeverityFilter] = useState('all');
  const [displayCount, setDisplayCount] = useState(10);

  const alertCounts = useMemo(() => {
    const counts = {
      all: alerts.length,
      warning: getAlertsBySeverity('warning').length,
      info: getAlertsBySeverity('info').length,
      error: getAlertsBySeverity('error').length
    };
    return counts;
  }, [alerts, getAlertsBySeverity]);

  const filteredAlerts = useMemo(() => {
    let filtered = alerts;

    if (severityFilter !== 'all') {
      filtered = getAlertsBySeverity(severityFilter);
    }

    return filtered.slice(0, displayCount);
  }, [alerts, severityFilter, displayCount, getAlertsBySeverity]);

  const getSeverityLabel = (severity) => {
    const labels = {
      warning: t('dashboardAlerts.severityLabels.warning'),
      info: t('dashboardAlerts.severityLabels.info'),
      error: t('dashboardAlerts.severityLabels.error')
    };
    return labels[severity] || t('dashboardAlerts.severityLabels.alert');
  };

  const getAlertAction = (alert) => {
    switch (alert.type) {
      case 'long-session':
        return {
          text: t('dashboardAlerts.actions.reviewSession'),
          action: () => {
            console.log('Navigate to staff detail:', alert.staffId);
          }
        };
      case 'long-break':
        return {
          text: t('dashboardAlerts.actions.checkBreak'),
          action: () => {
            console.log('Check break status:', alert.staffId);
          }
        };
      default:
        return null;
    }
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000);
    const isGerman = i18n.language === 'de';

    if (diff < 60) {
      return t('dashboardAlerts.timeRelative.justNow');
    } else if (diff < 3600) {
      const minutes = Math.floor(diff / 60);
      if (isGerman) {
        return `vor ${minutes} Min`;
      }
      return `${minutes}${t('dashboardAlerts.timeRelative.minutesAgo')}`;
    } else if (diff < 86400) {
      const hours = Math.floor(diff / 3600);
      if (isGerman) {
        return `vor ${hours} Std`;
      }
      return `${hours}${t('dashboardAlerts.timeRelative.hoursAgo')}`;
    } else {
      return timestamp.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit'
      });
    }
  };

  const handleSeverityFilter = (severity) => {
    setSeverityFilter(severity);
    setDisplayCount(10);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  const dismissAlert = (alertId) => {
    console.log('Dismiss alert:', alertId);
  };

  const clearAllFilters = () => {
    setSeverityFilter('all');
    setDisplayCount(10);
  };

  if (alertsLoading) {
    return (
      <div className="dashboard-alerts-widget">
        <div className="widget-header">
          <h3>{t('dashboardAlerts.title')}</h3>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('dashboardAlerts.loading')}</p>
        </div>
      </div>
    );
  }

  if (alertsError) {
    return (
      <div className="dashboard-alerts-widget">
        <div className="widget-header">
          <h3>{t('dashboardAlerts.title')}</h3>
        </div>
        <div className="error-state">
          <p className="error-message">× {alertsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-alerts-widget">
      <div className="widget-header">
        <h3>{t('dashboardAlerts.title')}</h3>
        <div className="stats-summary">
          {alertCounts.error > 0 && (
            <span className="stat error">
              <strong>{alertCounts.error}</strong> {t('dashboardAlerts.stats.critical')}
            </span>
          )}
          {alertCounts.warning > 0 && (
            <span className="stat warning">
              <strong>{alertCounts.warning}</strong> {t('dashboardAlerts.stats.warnings')}
            </span>
          )}
          {alertCounts.info > 0 && (
            <span className="stat info">
              <strong>{alertCounts.info}</strong> {t('dashboardAlerts.stats.info')}
            </span>
          )}
          {alertCounts.all === 0 && (
            <span className="stat success">
              <strong>{t('dashboardAlerts.stats.allClear')}</strong>
            </span>
          )}
        </div>
      </div>

      {alertCounts.all > 0 && (
        <div className="widget-filters">
          <div className="severity-filters">
            <button
              className={`severity-filter-btn ${severityFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleSeverityFilter('all')}
            >
              {t('dashboardAlerts.filters.allAlerts')} ({alertCounts.all})
            </button>

            {alertCounts.error > 0 && (
              <button
                className={`severity-filter-btn error ${severityFilter === 'error' ? 'active' : ''}`}
                onClick={() => handleSeverityFilter('error')}
              >
                {t('dashboardAlerts.severityLabels.error')} ({alertCounts.error})
              </button>
            )}

            {alertCounts.warning > 0 && (
              <button
                className={`severity-filter-btn warning ${severityFilter === 'warning' ? 'active' : ''}`}
                onClick={() => handleSeverityFilter('warning')}
              >
                {t('dashboardAlerts.severityLabels.warning')} ({alertCounts.warning})
              </button>
            )}

            {alertCounts.info > 0 && (
              <button
                className={`severity-filter-btn info ${severityFilter === 'info' ? 'active' : ''}`}
                onClick={() => handleSeverityFilter('info')}
              >
                {t('dashboardAlerts.severityLabels.info')} ({alertCounts.info})
              </button>
            )}
          </div>
        </div>
      )}

      {filteredAlerts.length === 0 ? (
        <div className="empty-state">
          {alerts.length === 0 ? (
            <>
              <div className="empty-icon">○</div>
              <h4>{t('dashboardAlerts.empty.allClear.title')}</h4>
              <p>{t('dashboardAlerts.empty.allClear.description')}</p>
            </>
          ) : (
            <>
              <div className="empty-icon">∅</div>
              <h4>{t('dashboardAlerts.empty.noResults.title')}</h4>
              <p>{t('dashboardAlerts.empty.noResults.description')}</p>
              <button
                onClick={clearAllFilters}
                className="clear-filters-btn"
              >
                {t('dashboardAlerts.actions.showAllAlerts')}
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="alerts-list">
            {filteredAlerts.map((alert) => {
              const alertAction = getAlertAction(alert);

              return (
                <div key={alert.id} className={`alert-item ${alert.severity}`}>
                  <div className={`alert-icon ${alert.severity}`}></div>

                  <div className="alert-content">
                    <div className="alert-header">
                      <span className="alert-title">
                        {getSeverityLabel(alert.severity)}: {alert.staffName}
                      </span>
                      <span className="alert-time">
                        {getRelativeTime(alert.timestamp)}
                      </span>
                    </div>

                    <div className="alert-message">
                      {alert.message}
                    </div>

                    <div className="alert-details">
                      <span className="department">
                        {alert.departmentName}
                      </span>
                      <span className="timestamp">
                        {formatTime(alert.timestamp)}
                      </span>
                    </div>
                  </div>

                  <div className="alert-actions">
                    {alertAction && (
                      <button
                        className="alert-action-btn primary"
                        onClick={alertAction.action}
                      >
                        {alertAction.text}
                      </button>
                    )}
                    <button
                      className="alert-action-btn secondary"
                      onClick={() => dismissAlert(alert.id)}
                      title={t('dashboardAlerts.actions.dismissAlert')}
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {displayCount < (severityFilter === 'all' ? alerts.length : getAlertsBySeverity(severityFilter).length) && (
            <div className="widget-footer">
              <div className="load-more-section">
                <button
                  onClick={handleLoadMore}
                  className="load-more-btn"
                >
                  {t('dashboardAlerts.actions.showMoreAlerts')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {filteredAlerts.length > 0 && (
        <div className="widget-footer">
          <p className="summary-text">
            {t('dashboardAlerts.summary.showing')} {filteredAlerts.length} {t('dashboardAlerts.summary.of')} {severityFilter === 'all' ? alerts.length : getAlertsBySeverity(severityFilter).length} {t('dashboardAlerts.summary.alerts')}
            {severityFilter !== 'all' && (
              <button
                onClick={clearAllFilters}
                className="clear-filters-link"
              >
                {t('dashboardAlerts.actions.clearFilters')}
              </button>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default DashboardAlerts;